/**
 * Pill numérique du graphique météo — 2 lignes (heure + valeur), ancrée au
 * point du curseur. Se repositionne pour rester visible dans le SVG :
 *  - à gauche du point si elle déborderait à droite ;
 *  - sous le point si elle déborderait en haut (cas du point le plus haut du
 *    graphe, où l'heure se faisait sinon couper par le bord supérieur).
 */

import type { VariableMeteo } from "./types.js";

// Décalages entre le point d'ancrage et la pill, et taille de la pill.
const GAP_X = 8;
const GAP_Y = 8;
const HEIGHT_PILL = 30;

export function PillValeur({
  xAncre,
  yAncre,
  widthLimite,
  valeur,
  unite,
  variableActive,
  timeIso,
  couleurFond = "#2B2A28",
}: {
  /** Abscisse du point pointé (centre de la pastille curseur). */
  xAncre: number;
  /** Ordonnée du point pointé (centre de la pastille curseur). */
  yAncre: number;
  widthLimite: number;
  valeur: number;
  unite: string;
  variableActive: VariableMeteo;
  /** ISO horaire du point pointé, pour afficher "Mer 14h" au-dessus. */
  timeIso: string;
  couleurFond?: string;
}) {
  // Format valeur : 1 décimale pour T° et pluie, entier pour vent/humidité.
  const texteValeur =
    variableActive === "temperature" || variableActive === "precipitation"
      ? `${valeur.toFixed(1)}${unite}`
      : `${Math.round(valeur)}${unite}`;
  // Pill 2 lignes : heure (ligne 1) + valeur en gras (ligne 2). Le jour est
  // déjà visible sur les libellés en bas du graphique et le panneau détail.
  const d = new Date(timeIso);
  const ligneHeure = `${String(d.getHours()).padStart(2, "0")}h`;
  const widthPill = Math.max(ligneHeure.length, texteValeur.length) * 7 + 12;

  // Horizontal : à droite du point par défaut, bascule à gauche si débordement.
  const xDroite = xAncre + GAP_X;
  const xPill = xDroite + widthPill > widthLimite ? xAncre - GAP_X - widthPill : xDroite;

  // Vertical : au-dessus du point par défaut, bascule en dessous si la pill
  // sortirait par le haut du SVG (sinon l'heure, sur la 1ère ligne, est coupée).
  const topAuDessus = yAncre - GAP_Y - HEIGHT_PILL;
  const yPill = topAuDessus < 2 ? yAncre + GAP_Y : topAuDessus;

  return (
    <g transform={`translate(${xPill}, ${yPill})`}>
      <rect x={0} y={0} rx={3} width={widthPill} height={HEIGHT_PILL} fill={couleurFond} />
      {/* Ligne 1 : heure (taille équivalente à la valeur, opacité 85%). */}
      <text
        x={6}
        y={12}
        fontSize={10}
        fontFamily="ui-monospace, monospace"
        fill="#fff"
        fillOpacity={0.85}
        letterSpacing="0.04em"
      >
        {ligneHeure}
      </text>
      {/* Ligne 2 : valeur (gras, primary) */}
      <text
        x={6}
        y={25}
        fontSize={11}
        fontWeight={600}
        fontFamily="ui-monospace, monospace"
        fill="#fff"
        letterSpacing="0.04em"
      >
        {texteValeur}
      </text>
    </g>
  );
}
